import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Link from 'next/link';
import React from 'react';
import NftImage from '../NftImage';
import WalletAddress from '../WalletAddress';

dayjs.extend(relativeTime);

const fetchRecentDonations = async () => {
  const response = await fetch('/api/donations/history?limit=20');
  if (!response.ok) {
    throw new Error('Failed to fetch donations');
  }
  const data = await response.json();
  return data.donations;
};

const DonationsFeed = () => {
  const {
    data: donations = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['recentDonations'],
    queryFn: fetchRecentDonations,
    refetchOnWindowFocus: false,
    refetchInterval: 30000,
    staleTime: 20000,
  });

  if (isLoading) {
    return (
      <div className='max-w-4xl mx-auto w-full mt-20'>
        <h2 className='text-center mb-4 text-white font-bold text-2xl'>Recent Donations</h2>
        <div className='rounded-lg bg-gray-900 p-8 max-h-[400px] overflow-y-scroll'>
          <div className='space-y-4'>
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className='animate-pulse flex space-x-4 p-3'>
                <div className='rounded-full bg-gray-700 h-10 w-10'></div>
                <div className='flex-1 space-y-2 py-1'>
                  <div className='h-4 bg-gray-700 rounded w-3/4'></div>
                  <div className='h-3 bg-gray-700 rounded w-1/2'></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className='max-w-4xl mx-auto w-full mt-20'>
        <h2 className='text-center mb-4 text-white font-bold text-2xl'>Recent Donations</h2>
        <div className='rounded-lg bg-gray-900 p-8 max-h-[400px] overflow-y-scroll'>
          <div className='text-center py-8 text-gray-400'>
            <p>Failed to load donations. Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-4xl mx-auto w-full mt-20'>
      <h2 className='text-center mb-4 text-white font-bold text-2xl'>Recent Donations</h2>
      <div className='rounded-lg bg-gray-900 p-2 md:p-8 max-h-[400px] overflow-y-scroll'>
        {donations.length === 0 ? (
          <div className='text-center py-8 text-gray-400'>
            <p>No donations yet. Be the first to contribute to the Shitty Vault!</p>
          </div>
        ) : (
          <ul className='space-y-1'>
            {donations.map((donation) => (
              <DonationsFeedItem key={donation.id} donation={donation} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const DonationsFeedItem = ({ donation }) => {
  const donorAddress = donation.donorAddress;
  const assets = donation.assets || [];
  const createdAt = donation.createdAt?.toDate ? donation.createdAt.toDate() : new Date(donation.createdAt);
  const isAnonymous = donation.anonymous;
  const txnId = donation.txnId;

  const explorerUrl = `https://allo.info/tx/${txnId}`;
  const firstAsset = assets[0];

  return (
    <li className='flex md:flex-row gap-4 flex-col justify-between odd:bg-gray-800 p-3 rounded-lg text-sm hover:bg-gray-700 transition-colors'>
      <div className='flex items-center gap-4 flex-1'>
        {firstAsset && (
          <div className='w-12 h-12 flex-shrink-0'>
            <NftImage nft={firstAsset} className='w-full h-full object-cover rounded-lg' />
          </div>
        )}

        <div className='flex-1 min-w-0'>
          <p className='text-gray-200'>
            🎁{' '}
            {isAnonymous ? (
              <span className='text-gray-400 font-medium'>Anonymous donor</span>
            ) : (
              <Link href={`/profile/${donorAddress}`}>
                <a className='text-white font-medium hover:text-primary-500 transition-colors'>
                  <WalletAddress address={donorAddress} />
                </a>
              </Link>
            )}{' '}
            donated{' '}
            <span className='text-primary-500 font-medium'>
              {assets.length} NFT{assets.length !== 1 ? 's' : ''}
            </span>{' '}
            to the Shitty Vault
          </p>

          {assets.length > 0 && (
            <div className='mt-1 flex flex-wrap gap-1'>
              {assets.slice(0, 3).map((asset, index) => (
                <span key={asset.id} className='text-xs text-gray-400'>
                  {asset.name || `Asset ${asset.id}`}
                  {index < Math.min(assets.length, 3) - 1 && ', '}
                </span>
              ))}
              {assets.length > 3 && <span className='text-xs text-gray-400'>and {assets.length - 3} more...</span>}
            </div>
          )}
        </div>
      </div>

      <div className='flex items-center gap-4 text-gray-400 flex-shrink-0'>
        <span className='text-xs'>{dayjs(createdAt).fromNow()}</span>
        {txnId && (
          <a
            href={explorerUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='text-xs hover:text-primary-500 transition-colors flex items-center gap-1'
          >
            <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor' className='w-3 h-3'>
              <path
                fillRule='evenodd'
                d='M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z'
                clipRule='evenodd'
              />
              <path
                fillRule='evenodd'
                d='M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z'
                clipRule='evenodd'
              />
            </svg>
            View
          </a>
        )}
      </div>
    </li>
  );
};

export default DonationsFeed;
